import Claim, { ClaimStatus } from '@/models/Claim';
import Project from '@/models/Project';
import { DateTime } from 'luxon';
import { falsey } from '.';
import { plusBusinessDays } from './dates';

export type NextActionTimeBar = {
  text: string;
  badgeClass: string;
  textClass: string;
  iconClass: string;
};

export type NextAction = {
  nextActionText: string;
  timeBar?: NextActionTimeBar;
};

function diffDueInDays(dueDate: DateTime): number {
  return Math.ceil(dueDate.startOf('day').diffNow('days').days);
}

function addPeriodToDate(
  startDate: DateTime,
  period: number,
  businessDays: boolean,
  holidays: Project['head_contract_settings']['holidays'],
): DateTime {
  return businessDays ?
    plusBusinessDays(startDate, holidays, period) :
    startDate.plus({ days: period });
}

function getClaimSendOutDueDate(selectedMonth: DateTime, project: Project): DateTime | undefined {
  const settings = project.subcontract_settings;

  if(falsey(settings.claims_due_on)) {
    return undefined;
  }

  const claimsDueDate = selectedMonth.set({ day: settings.claims_due_on });

  return addPeriodToDate(
    claimsDueDate,
    -settings.receipt_of_claim_period,
    settings.receipt_of_claim_business_days,
    project.head_contract_settings.holidays,
  );
}

function getLiteClaimDueDate(claim: Claim, project: Project): DateTime {
  return addPeriodToDate(
    claim.createdAt,
    project.subcontract_settings.receipt_of_claim_period,
    project.subcontract_settings.receipt_of_claim_business_days,
    project.head_contract_settings.holidays,
  );
}

function getLiteClaimCreateDueDate(project: Project, selectedMonth: DateTime): DateTime {
  return selectedMonth.set({ day: project.subcontract_settings.claims_due_on });
}

function getStandardClaimDueDate(claim: Claim, project: Project): DateTime {
  return addPeriodToDate(
    claim.draftIssuedAt,
    project.subcontract_settings.receipt_of_claim_period,
    project.subcontract_settings.receipt_of_claim_business_days,
    project.head_contract_settings.holidays,
  );
}

function getCertificateDueDate(claim: Claim, project: Project): DateTime | undefined {
  if(!claim.claimIssuedAt) {
    return undefined;
  }

  return addPeriodToDate(
    claim.claimIssuedAt,
    project.subcontract_settings.issue_of_certificate_period,
    project.subcontract_settings.issue_of_certificate_business_days,
    project.head_contract_settings.holidays,
  );
}

export function getClaimDueIn(
  claim: Claim | undefined,
  project: Project | undefined,
  selectedMonth: DateTime,
): number | undefined {
  if(!project) {
    return undefined;
  }

  if(project.feature_flag_subcontractor_claims_lite) {
    return falsey(claim) ?
      diffDueInDays(getLiteClaimCreateDueDate(project, selectedMonth)) :
      diffDueInDays(getLiteClaimDueDate(claim, project));
  }

  if(falsey(claim)) {
    const sendOutDueDate = getClaimSendOutDueDate(selectedMonth, project);

    return sendOutDueDate ? diffDueInDays(sendOutDueDate) : undefined;
  }

  return diffDueInDays(getStandardClaimDueDate(claim, project));
}

export function getCertificateDueIn(claim: Claim | undefined, project: Project | undefined): number | undefined {
  if(!project || !claim) {
    return undefined;
  }

  const dueDate = getCertificateDueDate(claim, project);

  return dueDate ? diffDueInDays(dueDate) : undefined;
}

function getNextLiteClaimActionLabel(claim: Claim | undefined): { label: string; softTimeBar: boolean } | undefined {
  if(falsey(claim)) {
    return { label: 'Create claim', softTimeBar: true };
  }

  if(claim.status === ClaimStatus.certified && claim.certificateIssuedAt) {
    return undefined;
  }

  if(claim.status === ClaimStatus.draft) {
    return { label: 'Subcontractor to submit claim', softTimeBar: false };
  }

  return { label: 'Issue certificate to subcontractor', softTimeBar: false };
}

function getNextClaimActionLabel(claim: Claim | undefined): { label: string; softTimeBar: boolean } | undefined {
  if(falsey(claim)) {
    return { label: 'Create claim', softTimeBar: true };
  }

  if(claim.status === ClaimStatus.certified && claim.certificateIssuedAt) {
    return undefined;
  }

  if(claim.status === ClaimStatus.draft) {
    return { label: 'Issue claim to subcontractor', softTimeBar: true };
  }

  if(claim.status === ClaimStatus.draft_subcontractor) {
    return { label: 'Subcontractor to submit claim', softTimeBar: false };
  }

  return { label: 'Issue certificate to subcontractor', softTimeBar: false };
}

function getNextActionLabel(
  claim: Claim | undefined,
  project: Project,
): { label: string; softTimeBar: boolean } | undefined {
  return project.feature_flag_subcontractor_claims_lite ?
    getNextLiteClaimActionLabel(claim) :
    getNextClaimActionLabel(claim);
}

function getNextLiteClaimDueDate(
  claim: Claim | undefined,
  project: Project,
  selectedMonth: DateTime,
): DateTime | undefined {
  if(falsey(claim)) {
    return getLiteClaimCreateDueDate(project, selectedMonth);
  }

  if(claim.status === ClaimStatus.draft) {
    return getLiteClaimDueDate(claim, project);
  }

  if(claim.certificateIssuedAt || !claim.claimIssuedAt) {
    return undefined;
  }

  return getCertificateDueDate(claim, project);
}

function getNextClaimDueDate(
  claim: Claim | undefined,
  project: Project,
  selectedMonth: DateTime,
): DateTime | undefined {
  if(falsey(claim)) {
    return getClaimSendOutDueDate(selectedMonth, project);
  }

  if(claim.status === ClaimStatus.draft) {
    return getClaimSendOutDueDate(selectedMonth, project);
  }

  if(claim.status === ClaimStatus.draft_subcontractor) {
    return getStandardClaimDueDate(claim, project);
  }

  if(claim.status === ClaimStatus.subcontractor_claim) {
    return getCertificateDueDate(claim, project);
  }

  if(claim.status === ClaimStatus.certified && !claim.certificateIssuedAt) {
    return getCertificateDueDate(claim, project);
  }

  return undefined;
}

function getNextDueDate(
  claim: Claim | undefined,
  project: Project,
  selectedMonth: DateTime,
): DateTime | undefined {
  return project.feature_flag_subcontractor_claims_lite ?
    getNextLiteClaimDueDate(claim, project, selectedMonth) :
    getNextClaimDueDate(claim, project, selectedMonth);
}

function formatDaysRemaining(dueIn: number): string {
  if(dueIn < 0) {
    return `overdue by ${Math.abs(dueIn)} days`;
  }

  if(dueIn === 0) {
    return 'due today';
  }

  if(dueIn === 1) {
    return '1 day remaining';
  }

  return `${dueIn} days remaining`;
}

function buildNextActionText(label: string, dueDate: DateTime, dueIn: number, isSoftTimeBar: boolean): string {
  let nextActionText = '';

  if(dueIn < 0 && !isSoftTimeBar) {
    nextActionText += 'Time barred - ';
  }

  nextActionText += `${label} by ${dueDate.toLocaleString()}, ${formatDaysRemaining(dueIn)}.`;

  return nextActionText;
}

function buildTimeBar(label: string, dueIn: number, isSoftTimeBar: boolean): NextActionTimeBar | undefined {
  if(dueIn < 0) {
    return {
      text: isSoftTimeBar ?
        `${label} overdue` :
        `Time barred ${label.toLowerCase()} overdue`,

      badgeClass: 'bg-red-100 text-red-800',
      textClass: 'text-red-600',
      iconClass: 'text-red-600',
    };
  }

  if(dueIn === 0) {
    return {
      text: `${label} due today`,
      badgeClass: 'bg-red-100 text-red-800',
      textClass: 'text-red-600',
      iconClass: 'text-red-600',
    };
  }

  if(dueIn < 5) {
    return {
      text: `${label} by ${dueIn} days`,
      badgeClass: 'bg-orange-100 text-orange-800',
      textClass: 'text-orange-600',
      iconClass: 'text-orange-600',
    };
  }

  return undefined;
}

export function getNextAction(
  claim: Claim | undefined,
  project: Project | undefined,
  selectedMonth: DateTime,
): NextAction {
  if(!project) {
    return {
      nextActionText: '',
      timeBar: undefined,
    };
  }

  const label = getNextActionLabel(claim, project);
  const dueDate = getNextDueDate(claim, project, selectedMonth);

  if(label === undefined || dueDate === undefined) {
    return {
      nextActionText: '',
      timeBar: undefined,
    };
  }

  const dueIn = diffDueInDays(dueDate);

  return {
    nextActionText: buildNextActionText(label.label, dueDate, dueIn, label.softTimeBar),
    timeBar: buildTimeBar(label.label, dueIn, label.softTimeBar),
  };
}

export function getNextTimeBarDueIn(
  claim: Claim | undefined,
  project: Project | undefined,
  selectedMonth: DateTime,
): number | undefined {
  if(!project) {
    return undefined;
  }

  const dueDate = getNextDueDate(claim, project, selectedMonth);

  return dueDate ? diffDueInDays(dueDate) : undefined;
}

export function getNextTimeBarDisplay(
  claim: Claim | undefined,
  project: Project,
  selectedMonth: DateTime,
): string | undefined {
  const nextAction = getNextAction(claim, project, selectedMonth);

  return nextAction.nextActionText || undefined;
}

export function getNextTimeBarSortKey(
  claim: Claim | undefined,
  project: Project | undefined,
  selectedMonth: DateTime,
): number {
  if(falsey(claim)) {
    return Number.NEGATIVE_INFINITY;
  }

  if(claim.certificateIssuedAt) {
    return Number.POSITIVE_INFINITY;
  }

  if(claim.status !== ClaimStatus.subcontractor_claim && claim.status !== ClaimStatus.certified && claim.submittedAt) {
    return Number.POSITIVE_INFINITY;
  }

  const dueIn = getNextTimeBarDueIn(claim, project, selectedMonth);

  return dueIn ?? Number.POSITIVE_INFINITY;
}

export function getNextTimeBarDueInTextClass(dueIn: number | undefined): string {
  if(dueIn === undefined) {
    return '';
  }

  if(dueIn < 0) {
    return 'text-red-600';
  }

  if(dueIn < 5) {
    return 'text-orange-600';
  }

  return 'text-gray-900';
}

export function getNextTimeBarDueInBadgeClass(dueIn: number | undefined, claim?: Claim): string {
  if(!claim) {
    return 'bg-red-100 text-red-800';
  }

  if(claim.status === ClaimStatus.certified && claim.certificateIssuedAt) {
    return 'bg-green-100 text-green-800';
  }

  if(dueIn === undefined) {
    return 'bg-gray-100 text-gray-800';
  }

  if(dueIn < 0) {
    return 'bg-red-100 text-red-800';
  }

  if(dueIn < 5) {
    return 'bg-orange-100 text-orange-800';
  }

  return 'bg-gray-100 text-gray-800';
}

export function getNextTimeBarDueInBadgeDotClass(dueIn: number | undefined, claim?: Claim): string {
  if(!claim) {
    return 'bg-red-400';
  }

  if(claim.status === ClaimStatus.certified && claim.certificateIssuedAt) {
    return 'bg-green-400';
  }

  if(dueIn === undefined) {
    return 'bg-gray-400';
  }

  if(dueIn < 0) {
    return 'bg-red-400';
  }

  if(dueIn < 5) {
    return 'bg-orange-400';
  }

  return 'bg-gray-400';
}
